import { transformAmount } from "@circle-react/components/Paywalls/Admin/PaywallsAdmin/FormModal/helpers/paywallCustomizationSerializer";
import type { useGetCouponApplication } from "@circle-react/components/Paywalls/Checkout/hooks/useGetCouponApplication";
import { CouponApplicationStatus } from "@circle-react/components/Paywalls/Checkout/hooks/useGetCouponApplication";
import {
  isOneTimePrice,
  isSubscriptionPrice,
} from "@circle-react/helpers/paywallPriceHelpers";
import { formatPaywallCurrencyAmount } from "@circle-react/helpers/paywalls/currencies";
import type { CouponApplication, Paywall } from "@circle-react/types/Paywall";
import type { ReducedPrice } from "@circle-react/types/PaywallCustomization";
import type { PaywallPrice } from "@circle-react/types/PaywallPrice";

import { t } from "@/i18n-js/instance";

import { defaultFormatPaywallCurrency } from "./constants";
import { Price } from "./Price";
import type { PriceComponentProps } from "./types";

const titleLocales = {
  subscription: (intervalType: string) => [
    "settings.paywall_prices.subscription.title",
    intervalType,
  ],
  onetime: () => ["settings.paywall_prices.onetime.type_qualifier"],
  installments: () => [],
};

const calculateReducedPrice = (
  price: Pick<PaywallPrice, "upfront_payment_amount" | "amount">,
  reducedPrice: ReducedPrice,
) => {
  // We need to satisfy the following scenarios
  // - Only the reduced upfront amount is bigger than the current upfront price;
  // - Only the reduced installment amount sum is bigger than the current installment price sum;
  // - Both the upfront amount and the installment amount is bigger than the current price;
  const upfrontAmount = price.upfront_payment_amount || 0;
  const installmentAmountSum = price.amount - upfrontAmount; // `price.amount` represents all the installments + the upfront on the backend
  const reducedUpfrontAmount = transformAmount(reducedPrice.upfront_amount);
  const reducedInstallmentAmountSum = transformAmount(reducedPrice.amount);
  let totalDiscount = 0;
  let priceOriginal = 0;

  if (reducedUpfrontAmount > upfrontAmount) {
    totalDiscount += reducedUpfrontAmount - upfrontAmount;
    priceOriginal += reducedUpfrontAmount;
  } else {
    priceOriginal += upfrontAmount;
  }

  if (reducedInstallmentAmountSum > installmentAmountSum) {
    totalDiscount += reducedInstallmentAmountSum - installmentAmountSum;
    priceOriginal += reducedInstallmentAmountSum;
  } else {
    priceOriginal += installmentAmountSum;
  }

  return {
    priceOriginal,
    totalDiscount,
  };
};

const getSubtitle = (
  price: Pick<PaywallPrice, "interval_type" | "price_type" | "amount">,
  paywall: Pick<Paywall, "currency">,
  couponApplications?: ReturnType<typeof useGetCouponApplication>,
  reducedPrice?: ReducedPrice,
) => {
  if (isSubscriptionPrice(price) || isOneTimePrice(price)) {
    return {
      subtitle: t(titleLocales[price.price_type](price.interval_type)),
    };
  }

  let priceOriginal = price.amount;
  let priceDiscounted = 0;
  let totalDiscount = 0;

  if (couponApplications?.status === CouponApplicationStatus.COUPON_APPLIED) {
    totalDiscount =
      couponApplications.applications?.reduce(
        (accumulator: number, x: CouponApplication) =>
          x.discount_amount + accumulator,
        0,
      ) || 0;
  } else if (reducedPrice) {
    const result = calculateReducedPrice(price, reducedPrice);
    priceOriginal = result.priceOriginal;
    totalDiscount = result.totalDiscount;
  }

  priceDiscounted = Math.max(0, priceOriginal - totalDiscount);

  const prefix =
    totalDiscount > 0
      ? formatPaywallCurrencyAmount(
          paywall.currency,
          priceOriginal,
          defaultFormatPaywallCurrency,
        )
      : undefined;

  return {
    prefix,
    subtitle: t(
      [
        "paywall_checkout.price_value.title",
        price.price_type,
        price.interval_type,
      ],
      {
        total_amount: formatPaywallCurrencyAmount(
          paywall.currency,
          priceDiscounted,
          defaultFormatPaywallCurrency,
        ),
      },
    ),
  };
};

export const PriceSubtitle = ({
  price,
  paywall,
  reducedPrice,
  couponApplications,
}: PriceComponentProps) => (
  <Price.Subtitle
    {...getSubtitle(price, paywall, couponApplications, reducedPrice)}
  />
);
